import React from "react";
import get from "lodash/get";
import { object, array, bool, string, number } from "prop-types";
import assetify from "@quintype/framework/assetify";
import cx from "classnames";
import { ResponsiveImage, WithLazy } from "@quintype/components";

import Icon from "../../icons";
import DefaultCardImage from "./default-card-image.svg";
import VideoBlackIcon from "../icons/VideoBlackIcon";
import GalleryBlackIcon from "../icons/GalleryBlackIcon";

import "./cardImage.m.css";

const CardImage = ({
  story = {},
  aspectRatio = [1, 1],
  defaultWidth = 500,
  imageType = "image1x1",
  zoom = true,
  widths = [250, 480, 764],
  eager = false,
  showStoryTypeIcon = true,
  showAlternateImage = false,
  darkTheme = false,
  largeIcon = false,
  isCommentedStory = false,
  datatrackingcategory = "",
  datatrackingaction = "",
  datatrackinglabel = "",
}) => {
  const wrapperStyleName = cx(
    "qt-image-dynamic",
    { image16x9: imageType === "image16x9" },
    { image4x3: imageType === "image4x3" },
    { image1x1: imageType === "image1x1" },
    { image2x1: imageType === "image2x1" },
    { image3x4: imageType === "image3x4" },
    { image33x47: imageType === "image33x47" },
    { image3x2: imageType === "image3x2" },
    { image1200x574: imageType === "image1200x574" },
    { image2x3: imageType === "image2x3" },
    { image1200x500: imageType === "image1200x500" }
  );

  let heroImage = "";
  let heroImageMetaData = "";
  if (showAlternateImage && get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"])) {
    heroImage = story.alternative.home.default["hero-image"]["hero-image-s3-key"];
    heroImageMetaData = story.alternative.home.default["hero-image"]["hero-image-metadata"];
  } else if (story["hero-image-s3-key"]) {
    heroImage = story["hero-image-s3-key"];
    heroImageMetaData = story["hero-image-metadata"];
  }

  if (typeof window === "undefined") {
    return (
      <figure>
        <picture className="qt-image">
          <img
            className="picture"
            src={`${assetify(DefaultCardImage)}`}
            alt={story["hero-image-caption"] || story.headline}
          />
        </picture>
      </figure>
    );
  }

  const MostCommentedStoryImage = () => (
    <figure styleName={wrapperStyleName} className={`${wrapperStyleName} qt-figure`}>
      <picture className={`qt-image ${zoom ? "zoom-desktop" : ""} ${story["hero-image-s3-key"] ? "" : "default"}`}>
        {story["hero-image-s3-key"] ? (
          <img src={story["hero-image-s3-key"]} alt={story.headline} className="qt-image" />
        ) : (
          <img
            className="picture"
            src={`${assetify(DefaultCardImage)}`}
            alt={story["hero-image-caption"] || story.headline}
            styleName="default-image"
          />
        )}
      </picture>
    </figure>
  );

  return (
    <WithLazy margin="50px">
      {() => (
        <div styleName="card-image-wrapper" className="card-image-wrapper">
          {isCommentedStory ? (
            <MostCommentedStoryImage />
          ) : (
            <figure styleName={wrapperStyleName} className={`${wrapperStyleName} qt-figure`}>
              {heroImage ? (
                <picture className={`qt-image ${zoom ? "zoom-desktop" : ""}`}>
                  <ResponsiveImage // Desktop WebP
                    slug={heroImage}
                    metadata={heroImageMetaData}
                    aspectRatio={aspectRatio}
                    defaultWidth={defaultWidth}
                    widths={widths}
                    type="image/webp"
                    sizes="( max-width: 500px ) 98%, ( max-width: 875px ) 48%, 23%"
                    imgParams={{ auto: ["format", "compress"], fmt: "webp" }}
                    styleName="image"
                    className="image"
                    alt={story["hero-image-caption"] || story.headline}
                    eager={eager}
                    data-tracking-category={datatrackingcategory}
                    data-tracking-action={datatrackingaction}
                    data-tracking-label={datatrackinglabel}
                  />
                </picture>
              ) : showAlternateImage ? (
                <picture className={`qt-image ${zoom ? "zoom-desktop default" : ""}`}>
                  <img
                    className="picture"
                    src={`${assetify(DefaultCardImage)}`}
                    alt={story["hero-image-caption"] || story.headline}
                    styleName="default-image"
                  />
                </picture>
              ) : null}
            </figure>
          )}
          {showStoryTypeIcon && (
            <span styleName="story-type-wrapper" className="story-type-wrapper">
              {story["story-template"] === "video" &&
                (darkTheme ? (
                  <VideoBlackIcon largeIcon={largeIcon} />
                ) : (
                  <Icon type="video-whitebg" className="video-whitebg" />
                ))}
              {story["story-template"] === "photo" &&
                (darkTheme ? (
                  <GalleryBlackIcon largeIcon={largeIcon} />
                ) : (
                  <Icon type="gallery-whitebg" className="gallery-whitebg" />
                ))}
            </span>
          )}
        </div>
      )}
    </WithLazy>
  );
};

CardImage.propTypes = {
  story: object,
  defaultWidth: number,
  imageType: string,
  widths: array,
  zoom: bool,
  eager: bool,
  aspectRatio: array,
  showStoryTypeIcon: bool,
  showAlternateImage: bool,
  darkTheme: bool,
  largeIcon: bool,
  isCommentedStory: bool,
  datatrackingcategory: string,
  datatrackingaction: string,
  datatrackinglabel: string,
};

export default CardImage;
